import React, { useRef, useState } from 'react';
import useOutsideClick from '../../utils/use-outside-click';

// Styles
import styles from './dropdownMenu.module.css';

interface MenuItem {
  text: string;
  onItemClicked?: () => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  submenu?: MenuItem[];
}

interface Props {
  buttonText: string;
  menuItems: MenuItem[];
  setOverflow?: boolean;
  submenuDirection?: 'left' | 'right';
}

const Dropdown: React.FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredSubmenu, setHoveredSubmenu] = useState<number | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setHoveredSubmenu(null);
  };

  useOutsideClick(menu, () => {
    setIsOpen(false);
    setHoveredSubmenu(null);
  });

  const handleItemClick = (index: number) => {
    const item = props.menuItems[index];
    if (item.submenu) {
      return;
    }

    const { onItemClicked, disabled } = item;
    if (!disabled && onItemClicked) {
      setIsOpen(false);
      setHoveredSubmenu(null);
      onItemClicked();
    }
  };

  const handleSubmenuItemClick = (item: MenuItem) => {
    const { onItemClicked, disabled } = item;
    if (!disabled && onItemClicked) {
      setIsOpen(false);
      setHoveredSubmenu(null);
      onItemClicked();
    }
  };

  const handleMouseEnter = (index: number) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }

    const item = props.menuItems[index];
    if (item.submenu) {
      setHoveredSubmenu(index);
    } else {
      setHoveredSubmenu(null);
    }
  };

  const handleMouseLeave = () => {
    submenuTimeoutRef.current = setTimeout(() => {
      setHoveredSubmenu(null);
    }, 150);
  };

  const handleSubmenuMouseEnter = () => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
  };

  const menuContent = props.menuItems.map((item, index) => (
    <div
      key={item.text}
      className={styles.submenuContainer}
      onMouseEnter={() => handleMouseEnter(index)}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={[styles.menuItem, item.disabled ? styles.disableBtn : ""].join(' ')}
        role="menuitem"
        style={item.style}
        onClick={() => handleItemClick(index)}
        disabled={item.disabled}
      >
        <span>{item.text}</span>
        {item.submenu && (
          <svg className={styles.chevron} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      {item.submenu && hoveredSubmenu === index && (
        <div
          className={[
            styles.submenu,
            props.submenuDirection === 'right' ? styles.submenuRight : ''
          ].join(' ')}
          onMouseEnter={handleSubmenuMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {item.submenu.map((subItem) => (
            <button
              key={subItem.text}
              className={[styles.submenuItem, subItem.disabled ? styles.disableBtn : ""].join(' ')}
              style={subItem.style}
              onClick={() => handleSubmenuItemClick(subItem)}
              disabled={subItem.disabled}
            >
              <span>{subItem.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  ));

  return (
    <div className={[styles.dropdown, props.setOverflow ? styles.relative : ''].join(' ')} ref={menu}>
      <button
        className={styles.button}
        type="button"
        aria-haspopup={true}
        aria-expanded={isOpen}
        aria-controls="headlessui-menu-items"
        onClick={toggleDropdown}
      >
        <span>{props.buttonText}</span>
        <svg className="w-5 h-5 ml-2 -mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
        </svg>
      </button>
      {isOpen && (
        <div className={styles.dropdownMenu} aria-labelledby="headlessui-menu-button" id="headlessui-menu-items" role="menu">
          {menuContent}
        </div>
      )}
    </div>
  );
};

export default Dropdown;

// How to use it
{/* <Dropdown
      buttonText="Button text"
      menuItems={[
        { text: 'item option text', onItemClicked: handleClick, style: { }, disabled: true },
      ]}
      submenuDirection='left | right'
/> */}